// DS8.1 (!) comes with consts:
import React, { useContext } from 'react';
import c from 'classnames';
import { ThemeOptions, ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ModalRoot, ModalRootProps, ModalCloseButton } from '../../base';
import ModalLayout, { ModalLayoutProps } from './ModalLayout';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';

import s from './Modal.scss';

export const MODAL_FOOTER_BUTTON_SIZE = 'large';
export const MODAL_FOOTER_PRIMARY_BUTTON_MIN_WIDTH = 100;

export interface Props
  // Partial here because TS has a bug that doesn't preserve optional-ness when extending and `Pick`ing
  extends
    Pick<
      ModalRootProps,
      | 'onRequestClose'
      | 'useFixedMobileHeight'
      | 'isFullscreen'
      | 'hasCloseButton'
      | 'useTransparentOverlay'
      | 'shouldCloseOnOverlayClick'
      | 'bodyScrollLockOptions'
      | 'contentLabel'
      | 'className'
      | 'preventScroll'
    >,
    Pick<
      ModalLayoutProps,
      'innerScrollRef' | 'contentClassName' | 'headerPadding' | 'footerPadding' | 'onSubmit'
    > {
  /** If `true`, this will show the modal. */
  isOpen: boolean;

  /** A heading that we place at the top of the modal. Don't apply any styling! */
  heading?: React.ReactNode;

  /** A subheading that we place below the `heading`. Don't apply any styling! */
  subheading?: React.ReactNode;

  /** The left side of the header. */
  headerLeft?: React.ReactNode;

  /** The right side of the header. */
  headerRight?: React.ReactNode;

  /** A className for the header. */
  headerClassName?: string;

  /** A slot just before the content. We tend to put controls here that don't fit in the heading or content. */
  preamble?: React.ReactNode;

  /** The left side of the footer. We tend to put disclaimers here. */
  footerLeft?: React.ReactNode[];

  /** The right side of the footer. We tend to put confirmation and cancel buttons here. */
  footerRight?: React.ReactNode[];

  /** Full width footer. */
  footer?: React.ReactNode;

  /** Show border under the header. Default is false */
  showHeaderBorder?: boolean;

  /** Show border over the footer. Default is false */
  showFooterBorder?: boolean;

  /** Wrap content in a scrollable div. Default is true */
  withScrollableContentWrapper?: boolean;

  /** Styles for the ModalLayout component */
  layoutClassName?: string;

  /**
   * If this modal is opened from within another modal/popover that doesn't close when this one mounts,
   * we need to use this prop or clicks on this modal's content will close the whole stack.
   * */
  ignoreClickOutside?: boolean;

  /**
   * Added to quell designers wrath in one specific instance. The BillingCyclePicker in da-ponies. Ideally, we will remove this
   * relatively soon. Or you'll see this comment in six years (should Earth survive that long, universe-willing) and you'll laugh.
   *
   * Don't use this prop. If a designer wants to make a modal `alwaysLight`, say no.
   *
   * @deprecated
   */
  alwaysLight?: boolean;

  /**
   * Enable ipad mode which deal with ipads being half desktop
   */
  ipadMode?: boolean;
}

/** Our Standard modal. Has a heading, subheading, close button, and footer.  */
const Modal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  heading,
  subheading,
  headerLeft,
  headerRight,
  headerClassName,
  preamble,
  footerLeft,
  footerRight,
  footer,
  headerPadding,
  footerPadding,
  showHeaderBorder,
  showFooterBorder,
  children,
  className,
  contentClassName,
  innerScrollRef,
  withScrollableContentWrapper = true,
  isFullscreen: isFullscreenProp,
  alwaysLight = false,
  layoutClassName,
  ignoreClickOutside,
  ipadMode = false,
  onSubmit,
  hasCloseButton = true,
  ...rest
}) => {
  const isMobile = useContext(MobileContext);
  const enableIpad =
    ipadMode &&
    typeof document !== 'undefined' &&
    navigator.userAgent.includes('Mac') &&
    'ontouchend' in document;
  const isFullscreen = (isFullscreenProp ?? isMobile) || enableIpad;

  return (
    <MobileContext.Provider value={isMobile || (enableIpad ?? false)}>
      <ModalRoot
        ipadMode={ipadMode}
        isOpen={isOpen}
        className={c(className, ignoreClickOutside && 'ignore-react-onclickoutside')}
        // We'll define it manually so that it respects the ThemeOverride
        hasCloseButton={false}
        isFullscreen={isFullscreen}
        {...(rest as any)}
      >
        <ThemeOverride
          className={s['theme-wrapper']}
          theme={alwaysLight ? ThemeOptions.LIGHT : undefined}
        >
          <ModalLayout
            className={layoutClassName}
            isFullscreen={isFullscreen}
            withScrollableContentWrapper={withScrollableContentWrapper}
            innerScrollRef={innerScrollRef}
            header={
              (heading || hasCloseButton) && (
                <>
                  {heading && (
                    <ModalHeader
                      className={headerClassName}
                      heading={heading}
                      subheading={subheading}
                      left={headerLeft}
                      right={headerRight}
                    />
                  )}
                  {hasCloseButton && <ModalCloseButton className={s['close-button']} />}
                </>
              )
            }
            preamble={preamble}
            content={children}
            contentClassName={contentClassName}
            footer={
              footer ||
              ((!!footerLeft || !!footerRight) && (
                <ModalFooter left={footerLeft} right={footerRight} />
              ))
            }
            headerPadding={headerPadding}
            footerPadding={footerPadding}
            headerBorder={showHeaderBorder}
            footerBorder={showFooterBorder}
            onSubmit={onSubmit}
          />
        </ThemeOverride>
      </ModalRoot>
    </MobileContext.Provider>
  );
};
Modal.displayName = 'Modal';

export default Modal;
